/**
 * Client-side CMS CCN validation for facility profile (SSOT-39 / #10163 / #10487).
 * Mirrors rules `validate_usa_medicare_ccn_for_profile` / `coerce_cms_ccn_for_enforcement_search`.
 */

const CMS_CCN_PLAUSIBLE = /^[0-9A-Za-z]{6,12}$/;

/** CMS/SSA numeric state prefix (CCN positions 1–2). */
export const CMS_SSA_STATE_PREFIX: Record<string, string> = {
  AL: "01", AK: "02", AZ: "03", AR: "04", CA: "05", CO: "06", CT: "07", DE: "08",
  DC: "09", FL: "10", GA: "11", HI: "12", ID: "13", IL: "14", IN: "15", IA: "16",
  KS: "17", KY: "18", LA: "19", ME: "20", MD: "21", MA: "22", MI: "23", MN: "24",
  MS: "25", MO: "26", MT: "27", NE: "28", NV: "29", NH: "30", NJ: "31", NM: "32",
  NY: "33", NC: "34", ND: "35", OH: "36", OK: "37", OR: "38", PA: "39", PR: "40",
  RI: "41", SC: "42", SD: "43", TN: "44", TX: "45", UT: "46", VT: "47", VI: "48",
  VA: "49", WA: "50", WV: "51", WI: "52", WY: "53",
};

const STATE_NAME_TO_ABBR: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", "district of columbia": "DC",
  florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID", illinois: "IL",
  indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY", louisiana: "LA",
  maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI", minnesota: "MN",
  mississippi: "MS", missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV",
  "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM", "new york": "NY",
  "north carolina": "NC", "north dakota": "ND", ohio: "OH", oklahoma: "OK",
  oregon: "OR", pennsylvania: "PA", "puerto rico": "PR", "rhode island": "RI",
  "south carolina": "SC", "south dakota": "SD", tennessee: "TN", texas: "TX",
  utah: "UT", vermont: "VT", "virgin islands": "VI", virginia: "VA",
  washington: "WA", "west virginia": "WV", wisconsin: "WI", wyoming: "WY",
};

export interface CcnValidationContext {
  state?: string | null;
  facilityTypeSlug?: string | null;
  providerBasedStatus?: string | null;
}

export function normalizeUsStateAbbr(state: string | null | undefined): string | null {
  if (!state) return null;
  const s = state.trim();
  if (!s) return null;
  if (s.length === 2) return s.toUpperCase();
  return STATE_NAME_TO_ABBR[s.toLowerCase()] ?? null;
}

export function isPlausibleCmsCcn(ccn: string): boolean {
  const s = (ccn || "").trim();
  if (!s) return false;
  if (!CMS_CCN_PLAUSIBLE.test(s)) return false;
  if (/^\d+$/.test(s) && s.length !== 6) return false;
  return true;
}

/** True when profile field_key is the CCN field. */
export function isCcnProfileFieldKey(fieldKey: string | undefined | null): boolean {
  return (fieldKey || "").trim().toLowerCase() === "ccn";
}

function isRhcFacilityType(slug: string | null | undefined): boolean {
  const s = (slug || "").toLowerCase().replace(/[-\s]+/g, "_");
  return s === "rural_health_clinic" || s.includes("rural_health_clinic") || s === "rhc";
}

function ccnQuad(ccn: string): number | null {
  const tail = ccn.trim().slice(-4);
  if (!/^\d{4}$/.test(tail)) return null;
  return Number(tail);
}

/** CMS RHC quads: independent 3400–3499 / 3800–3974, provider-based 3975–3999, plus 8500–8999. */
function isRhcQuad(quad: number): boolean {
  return (
    (quad >= 3400 && quad <= 3499) ||
    (quad >= 3800 && quad <= 3999) ||
    (quad >= 8500 && quad <= 8999)
  );
}

function isProviderBasedRhcQuad(quad: number): boolean {
  return quad >= 3975 && quad <= 3999;
}

/**
 * Validate a profile CCN value. Empty is allowed (required-field checks handle that).
 * Returns an error message, or null when valid / empty.
 */
export function validateCcnProfileValue(
  value: unknown,
  context: CcnValidationContext = {},
): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  if (s.startsWith("FAC-")) {
    return "CCN cannot be an internal facility id";
  }
  if (!isPlausibleCmsCcn(s)) {
    return "Enter a valid 6-digit CMS Certification Number (not an NPI)";
  }

  const abbr = normalizeUsStateAbbr(context.state);
  const expectedPrefix = abbr ? CMS_SSA_STATE_PREFIX[abbr] : null;
  if (expectedPrefix && /^\d{6}$/.test(s) && s.slice(0, 2) !== expectedPrefix) {
    return `CCN must use the ${abbr} CMS state prefix ${expectedPrefix}`;
  }

  if (isRhcFacilityType(context.facilityTypeSlug) && /^\d{6}$/.test(s)) {
    const quad = ccnQuad(s);
    if (quad == null || !isRhcQuad(quad)) {
      return "CCN is not in a Rural Health Clinic CMS type range";
    }
    const pb = (context.providerBasedStatus || "").toLowerCase();
    if ((pb === "provider_based" || pb.includes("provider-based") || pb.includes("provider_based"))
      && !isProviderBasedRhcQuad(quad)) {
      return "Provider-based RHC CCN must be in CMS range 3975-3999";
    }
  }

  return null;
}
